import axiosTokenInterceptor from "interceptors/tokenInterceptor";
import { useErrorHandler } from "contexts/errorContext";
import { useEffect, useRef, useState } from "react";
import { createSearchParams, useNavigate } from "react-router";
import { ProductTypes } from "utils/constants/enums";
import { getCachedConfiguration, getPublisherFlags } from "utils/functions/configurationUtil";
import { isSupplierSurveyMode } from "utils/functions/supplierContextUtil";
import * as systemNotificationUtils from "utils/functions/systemNotificationUtil";

interface RedirectCallbackResult {
  redirectLink: string;
  productType: string;
}

function useInBrainCallbackRedirect() {
  const handleError = useErrorHandler();
  const navigate = useNavigate();

  const configuration = getCachedConfiguration();
  const hashedPublisherAppUid = configuration?.hashed_publisher_app_uid ?? "";
  const isNativeSurveysReturnToAppEnabled = getPublisherFlags()!.isNativeSurveysInAppRoutingEnabled;
  const isSupplierSurvey = isSupplierSurveyMode();

  const [isLoading, setIsLoading] = useState(true);

  const firstRender = useRef(true);

  useEffect(() => {
    if (!firstRender.current) return;
    firstRender.current = false;

    fetchRedirectLink();
  }, []);

  function navigateToSurveys() {
    navigate({
      pathname: "/surveys",
      search: createSearchParams({ reward: "" }).toString(),
    });
  }

  function fetchRedirectLink() {
    const url = `${process.env.API_URL}/external-provider-callback/dynata-engage/redirect`;

    axiosTokenInterceptor
      .get(url, {
        params: {
          appUserId: hashedPublisherAppUid,
        },
      })
      .then(({ data }: { data: RedirectCallbackResult }) => {
        const productType = ProductTypes[data.productType as keyof typeof ProductTypes];

        if (configuration?.frame_origin) {
          window.opener.postMessage("return-to-surveys", window.location.origin);
          window.close();
          return;
        }

        if (productType == ProductTypes.NativeSurveys) {
          if (isNativeSurveysReturnToAppEnabled) {
            systemNotificationUtils.notifyNativeSurveyClosed();
            return;
          }
          setIsLoading(false);
          navigateToSurveys();
          return;
        }

        if (productType == ProductTypes.SupplierApi) {
          systemNotificationUtils.notifySurveyClosed();

          if (data.redirectLink) {
            window.location.replace(data.redirectLink);
            return;
          }

          if (isSupplierSurvey) {
            navigate({ pathname: "/error" });
            return;
          }
        }

        // Default case
        systemNotificationUtils.notifySurveyClosed();
        setIsLoading(false);
        navigateToSurveys();
      })
      .catch((error) => {
        setIsLoading(false);
        handleError(error);
      });
  }

  return {
    isLoading,
  };
}

export default useInBrainCallbackRedirect;
